import {FC, useEffect, useState} from 'react'
import {useHistory, useParams} from 'react-router-dom'
import {
  PublicExpert,
  PublicExpertiseService,
  getExpertProfile,
  getExpertServices,
} from '../../services/expertiseSearchService'
import {handleApiError} from '../../utils/handleApiError'
import {MemberProfile} from '../members/components/MemberProfile'
import {MemberImage} from '../members/components/MemberImage'
import {ExpertiseOfferCard} from './components/ExpertiseOfferCard'

/**
 * Ficha publica de un Partner tipo Expert (Figma 4605:85192).
 *
 * Reusa `MemberProfile` y `MemberImage`, los mismos componentes que la ficha de un miembro, en vez
 * de reimplementar el encabezado: asi el nombre en Cormorant, el divisor, la ubicacion y el bloque
 * de foto salen identicos sin duplicar una linea de estilo.
 *
 * Las diferencias con el diseno del miembro son las que pidio el negocio:
 *  - sin banner "invites you" y sin "Request Introduction": una introduccion se le pide a un
 *    MIEMBRO, y esto es una empresa. La unica accion es Book Expertise;
 *  - sin el tag "Member", porque no lo es.
 *
 * Y una que impone el modelo: solo dos tabs. Un Partner no tiene lifestyle ni reconocimientos —
 * esos tabs del diseno se alimentan del perfil de un miembro y aqui no hay de donde sacarlos.
 */
const ExpertProfilePage: FC = () => {
  const {partnerId} = useParams<{partnerId: string}>()
  const history = useHistory()
  const [expert, setExpert] = useState<PublicExpert | null>(null)
  const [services, setServices] = useState<PublicExpertiseService[]>([])
  const [activeTab, setActiveTab] = useState<'biography' | 'expertise'>('expertise')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let alive = true
    setLoading(true)
    Promise.all([getExpertProfile(partnerId), getExpertServices(partnerId)])
      .then(([profile, list]) => {
        if (!alive) return
        setExpert(profile)
        setServices(list)
      })
      .catch((error) => alive && handleApiError(error))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [partnerId])

  if (loading) return <div className='members-page__no-results'>Loading...</div>
  if (!expert) return <div className='members-page__no-results'>Expert not found</div>

  const tabs = [
    {id: 'biography' as const, label: 'Biography'},
    {id: 'expertise' as const, label: 'Expertise'},
  ]

  return (
    <div className='member-detail expert-profile'>
      <div className='help-center-sections w-100 tap-flex-center justify-content-start mb-5'>
        <div className='nb-link-underline-500 w-auto cursor-pointer' onClick={() => history.goBack()}>
          Back
        </div>
      </div>

      <div className='member-detail__container'>
        <div className='member-detail__left'>
          <div className='member-detail__info-section'>
            <span className='expert-profile__tag'>Expert</span>

            <MemberProfile
              firstName={expert.companyName}
              surname=''
              bioPresentation={expert.description || ''}
              city={expert.address || ''}
            />
          </div>

          {/* Unica accion de la ficha. Sin servicios publicados no lleva a ningun lado, asi que no
              se pinta en vez de dejar un boton que abre una lista vacia. */}
          {services.length > 0 && (
            <button
              type='button'
              className='expert-profile__book'
              onClick={() => history.push(`/experts/${expert.id}/book`)}
            >
              <span>Book Expertise</span>
              <svg width='13' height='4' viewBox='0 0 13.3502 3.88356' fill='none' xmlns='http://www.w3.org/2000/svg' aria-hidden='true'>
                <path d='M8.5 0.379406L12 3.38356H0' stroke='white' />
              </svg>
            </button>
          )}
        </div>

        {/* Sin `picFooter`: la cita al pie del retrato es del perfil de un miembro y un partner no
            tiene una. Se deja vacia en vez de inventar texto. */}
        <MemberImage profilePicture={expert.logo || ''} picFooter='' loading={loading} />
      </div>

      <div className='member-detail__tabs-section'>
        <div className='nb-pm-tabs-list'>
          {tabs.map((tab, index) => (
            <div className='tap-flex-center' key={tab.id}>
              <div
                className={`nb-pm-tabs-item ${activeTab === tab.id ? 'nb-pm-tabs-item--active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <div className='nb-pm-tabs-item-text'>{tab.label}</div>
              </div>
              {index < tabs.length - 1 && (
                <svg className='ml_3' width='1' height='20' viewBox='0 0 1 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
                  <path d='M0.5 0V20' stroke='#B4B4B4' />
                </svg>
              )}
            </div>
          ))}
        </div>

        <div className='bio-tabs__content'>
          {activeTab === 'biography' && (
            <p className='expert-profile__bio'>
              {expert.description || 'This expert has not added a description yet.'}
            </p>
          )}

          {activeTab === 'expertise' && (
            <div className='expertise-offer-list'>
              {services.length ? (
                services.map((service) => (
                  <ExpertiseOfferCard key={service.id} service={service} variant='static' />
                ))
              ) : (
                <div className='members-page__no-results'>No expertise available</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export {ExpertProfilePage}
export default ExpertProfilePage
